import type { ReactNode } from 'react';
import { RequiredMark } from '@/components/form/Field';
import dayjs from '@/lib/dayjs';
import { formatMinutes } from '@/lib/duration';
import type TimeEntryModel from '@/models/entities/TimeEntryModel';
import { useCompanyTimezone } from '@/stores/useCompanyStore';

/**
 * Briques des lignes d'une feuille d'heures, saisie (Feuille d'heure) et lecture (détail d'une demande) : tableau sur
 * grand écran, cartes sur mobile (useIsMobile)
 */

/** En-tête du tableau ; actions : colonne des actions et saisie (brouillon) : « * » sur les colonnes obligatoires */
export function TimeEntryHead({ actions = false }: { actions?: boolean }) {
  return (
    <thead>
      <tr className="bg-slate-50 border-b border-slate-200 text-xs font-semibold text-slate-500 uppercase tracking-wider">
        <th className="px-4 py-3 w-40">
          Date
          {actions && <RequiredMark />}
        </th>
        <th className="px-4 py-3 w-28">
          Début
          {actions && <RequiredMark />}
        </th>
        <th className="px-4 py-3 w-28">
          Fin
          {actions && <RequiredMark />}
        </th>
        <th className="px-4 py-3 w-20">Durée</th>
        <th className="px-4 py-3 w-64">Projet</th>
        <th className="px-4 py-3">Commentaires</th>
        {actions && (
          <th className="px-4 py-3 w-28">
            <span className="sr-only">Actions</span>
          </th>
        )}
      </tr>
    </thead>
  );
}

/** Cellules d'une ligne enregistrée, dans l'ordre de TimeEntryHead (sans les actions), à l'heure de la société */
export function TimeEntryCells({ entry }: { entry: TimeEntryModel }) {
  const { day, startTime, endTime } = entry.localIn(useCompanyTimezone());
  return (
    <>
      <td className="px-4 py-3 whitespace-nowrap">{dayjs(day).format('ddd DD/MM/YYYY')}</td>
      <td className="px-4 py-3">{startTime}</td>
      <td className="px-4 py-3">{endTime}</td>
      <td className="px-4 py-3">
        <DurationPill minutes={entry.durationMinutes} />
      </td>
      <td className="px-4 py-3 font-medium text-slate-900">{entry.project}</td>
      <td className="px-4 py-3 text-slate-500">{entry.comment}</td>
    </>
  );
}

/**
 * Ligne enregistrée en carte, sur mobile (à la place du tableau, trop large) : date et durée, plage horaire, projet et
 * commentaire ; actions : son menu ⋮ (saisie), à droite. Dans une liste (TimeEntryCards).
 */
export function TimeEntryCard({ entry, actions }: { entry: TimeEntryModel; actions?: ReactNode }) {
  const { day, startTime, endTime } = entry.localIn(useCompanyTimezone());
  return (
    <li className="p-4 flex items-start gap-2 text-sm text-slate-700" data-testid="time-entry-row">
      <div className="flex-1 min-w-0 space-y-1">
        <div className="flex items-center justify-between gap-2">
          <p className="font-semibold text-slate-900 capitalize">{dayjs(day).format('ddd DD/MM/YYYY')}</p>
          <DurationPill minutes={entry.durationMinutes} />
        </div>
        <p className="tabular-nums text-slate-600" data-testid="time-entry-range">
          {startTime} → {endTime}
        </p>
        {entry.project && (
          <p className="font-medium text-slate-900 break-words" data-testid="time-entry-project">
            {entry.project}
          </p>
        )}
        {entry.comment && (
          <p className="text-slate-500 break-words whitespace-pre-line" data-testid="time-entry-comment">
            {entry.comment}
          </p>
        )}
      </div>
      {actions && <div className="-mr-2 -mt-1 shrink-0">{actions}</div>}
    </li>
  );
}

/** Liste des cartes (TimeEntryCard) */
export function TimeEntryCards({ children }: { children: ReactNode }) {
  return <ul className="divide-y divide-slate-200">{children}</ul>;
}

/** Durée d'une ligne (« 2h30 »), « – » tant qu'elle est nulle (saisie incomplète) */
export function DurationPill({ minutes }: { minutes: number }) {
  return (
    <span className="text-sm font-semibold text-slate-600 bg-slate-100 px-2 py-1 rounded">
      {minutes ? formatMinutes(minutes) : '–'}
    </span>
  );
}
